import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { Account } from 'src/app/auth/models/user.model';
import { AuthService } from 'src/app/auth/services';
import { FamilyBookingService } from 'src/app/schedule-introductory-lesson/services/family-booking.service';
import { BaseComponent } from 'src/app/shared/components/base-component/base.component';
import { APP_CONFIG, Constants, MenuConfig, ROUTER_PATHS } from 'src/app/shared/constants';
import { MenuConfigService } from 'src/app/shared/services';
import { LocalStorageService, StorageItem } from 'src/app/shared/services/local-storage.service';
import { MessagingService } from 'src/app/shared/services/messaging.service';
import { SignalRService } from 'src/app/shared/services/signalr.service';

@Component({
  selector: 'app-authorized-layout',
  templateUrl: './authorized-layout.component.html',
  styleUrls: ['./authorized-layout.component.scss', '../../shared/components/splash-screen/splash-screen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthorizedLayoutComponent extends BaseComponent implements OnInit {
  appConfig = APP_CONFIG;
  isMobileDevice!: boolean;
  isExpanded!: boolean;
  menuItems: any;

  constructor(
    private readonly authService: AuthService,
    private readonly menuConfigService: MenuConfigService,
    private readonly cdr: ChangeDetectorRef,
    private readonly signalRService: SignalRService,
    private readonly messagingService: MessagingService,
    private readonly router: Router,
    private readonly localStorageService: LocalStorageService,
    private readonly familyBookingService: FamilyBookingService
  ) {
    super();
  }

  getIsMobile(): boolean {
    const w = document.documentElement.clientWidth;
    const breakpoint = 1000;
    return w > breakpoint;
  }

  ngOnInit(): void {
    this.isMobileDevice = !this.getIsMobile();
    this.getCurrentUser();
    this.signalRService.startConnection();
  }

  @HostListener('window:resize', ['$event']) onResize() {
    this.isMobileDevice = !this.getIsMobile();
  }

  getCurrentUser(): void {
    this.showPageLoader = true;
    this.authService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.userRole) {
            this.menuConfigService.modifyMenuConfig(MenuConfig, res?.userRole);
            this.menuConfigAccordingToPermission();
            this.currentUser = res;
            this.performFirebaseActions();
            this.clearInquiryIfNotOwnedBy(res);
            this.redirectIfPendingIntroductoryLesson(res);
            this.showPageLoader = false;
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.showPageLoader = false;
          this.cdr.detectChanges();
        }
      });
  }

  performFirebaseActions(): void {
    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission === 'granted') {
      this.initFirebaseMessaging();
      return;
    }

    if (Notification.permission === 'denied') {
      return;
    }

    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        this.initFirebaseMessaging();
      }
    });
  }

  private initFirebaseMessaging(): void {
    if (localStorage.getItem(StorageItem.FirebaseRegistrationToken)) {
      return;
    }
    this.messagingService.getFirebaseToken();
    this.messagingService.receiveMessage();
    this.messagingService.currentMessage.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (res) {
        console.log('message recieved', res);
      }
    });
  }

  clearInquiryIfNotOwnedBy(user: Account): void {
    if (this.familyBookingService.getInquiry() && !this.familyBookingService.isInquiryOwnedBy(user.emailAddress)) {
      this.familyBookingService.clearInquiry();
    }
  }

  redirectIfPendingIntroductoryLesson(user: Account): void {
    if (user.userRole !== Constants.roles.USER) {
      return;
    }
    if (localStorage.getItem(StorageItem.PendingIntroductoryLesson)) {
      this.router.navigate([ROUTER_PATHS.scheduleClasses.root, ROUTER_PATHS.scheduleClasses.introductoryLesson]);
      return;
    }
    if (!this.localStorageService.getItem(StorageItem.PendingIntroductoryLessons)) {
      return;
    }
    if (!this.familyBookingService.isInquiryOwnedBy(user.emailAddress)) {
      return;
    }
    this.router.navigate([ROUTER_PATHS.bookIntroductoryLesson]);
  }

  menuConfigAccordingToPermission() {
    this.menuItems = this.menuConfigService.getMenuConfig();
  }
}
